import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  useMaterialReactTable,
  MaterialReactTable,
} from "material-react-table";
import { useTheme } from "@emotion/react";
import { CustomThemeType } from "../../context/Providers";
import DashboardLayout from "../../layouts/DashboardLayout";
import UserIcon from "@mui/icons-material/Store";
import type { Dealer } from "../../types/DB2";
import { useUserData } from "../../context/UserDataFetcher";
import { DealersDataProvider, useDealersData } from "../../context/DealersDataProvider";
import { getFirestore, collection, getDocs, query, where, doc, getDoc, setDoc } from "firebase/firestore";

const logVerbose = (message: string, payload?: any) => {
  try {
    if (payload !== undefined) {
      console.debug(`[ManageDealers] ${message}`, payload);
    } else {
      console.debug(`[ManageDealers] ${message}`);
    }
  } catch {
    /* ignore console failures */
  }
};

type FormDealer = Partial<Dealer> & {
  principalEmail?: string;
  dealerNumber?: string; // number assigned by current manufacturer
};

type DealerUserSummary = {
  id: string;
  email?: string;
  name?: string;
  role?: string;
  isAdmin?: boolean;
};

const DealerModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onSave: (payload: FormDealer) => Promise<void> | void;
  dealer?: Dealer | null;
}> = ({ open, onClose, onSave, dealer }) => {
  const isEdit = !!dealer;
  const [form, setForm] = useState<FormDealer>({});
  const userData = useUserData();
  const [errors, setErrors] = useState<{ dealerNumber?: string }>({});

  useEffect(() => {
    logVerbose("DealerModal opened", {
      isEdit,
      dealerId: dealer?.id ?? null,
      manufacturerId: userData?.manufacturerId ?? null,
    });
    if (dealer) {
      const mf = userData?.manufacturerId;
      const existingNumber = mf ? (dealer as any)?.manufacturerDealerNumbers?.[mf] : undefined;
      setForm({
        name: dealer.name,
        contactInfo: dealer.contactInfo,
        dealerNumber: existingNumber || "",
      });
    } else {
      setForm({});
    }
  }, [dealer, open, userData?.manufacturerId]);

  const handleChange = (k: keyof FormDealer, v: any) => {
    setForm((s) => ({ ...s, [k]: v }));
    logVerbose("DealerModal field changed", { field: k, value: v });
  };

  const handleSubmit = async () => {
    // client-side required validation for dealer number
    const nextErrors: { dealerNumber?: string } = {};
    if (!form.dealerNumber || !String(form.dealerNumber).trim()) {
      nextErrors.dealerNumber = "Dealer Number is required";
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const out: FormDealer = { ...form };
    // prune empty
    Object.keys(out).forEach((k) => {
      const v = (out as any)[k];
      if (v === "" || v === undefined || v === null) delete (out as any)[k];
    });
    await onSave(out);
    logVerbose("DealerModal submitted", { isEdit, dealerId: dealer?.id ?? null });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? "Edit Dealer" : "Add Dealer"}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            label="Business Name"
            value={form.name ?? ""}
            onChange={(e) => handleChange("name", e.target.value)}
            fullWidth
            required
          />
          <TextField
            label="Contact Email"
            value={form.contactInfo?.email ?? ""}
            onChange={(e) =>
              handleChange("contactInfo", {
                ...(form.contactInfo || {}),
                email: e.target.value,
              })
            }
            fullWidth
            required
          />
          <TextField
            label="Contact Phone"
            value={form.contactInfo?.phone ?? ""}
            onChange={(e) =>
              handleChange("contactInfo", {
                ...(form.contactInfo || {}),
                phone: e.target.value,
              })
            }
            fullWidth
          />
          <TextField
            label="Contact Address"
            value={form.contactInfo?.address ?? ""}
            onChange={(e) =>
              handleChange("contactInfo", {
                ...(form.contactInfo || {}),
                address: e.target.value,
              })
            }
            fullWidth
          />
          {/* Manufacturer IDs are managed in the background and not user-editable */}
          <TextField
            label="Dealer Number"
            value={form.dealerNumber ?? ""}
            onChange={(e) => {
              if (errors.dealerNumber) setErrors((s) => ({ ...s, dealerNumber: undefined }));
              handleChange("dealerNumber", e.target.value);
            }}
            required
            error={!!errors.dealerNumber}
            helperText={errors.dealerNumber}
            fullWidth
          />
          <TextField
            label="Principal User Email (optional)"
            value={form.principalEmail ?? ""}
            onChange={(e) => handleChange("principalEmail", e.target.value)}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained">
          {isEdit ? "Save" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const InnerManageDealers: React.FC = () => {
  const userData = useUserData();
  const theme: CustomThemeType = useTheme() as CustomThemeType;
  const db = getFirestore();
  const updatePrincipalUserDoc = useCallback(
    async (uid: string, dealerDocId: string) => {
      if (!uid || !dealerDocId) return;
      try {
        const payload: Record<string, any> = { dealerId: dealerDocId };
        if (userData?.manufacturerId) {
          payload.manufacturerId = userData.manufacturerId;
        }
        await setDoc(doc(db, "users", uid), payload, { merge: true });
        logVerbose("Principal user document linked to dealer", {
          uid,
          dealerId: dealerDocId,
        });
      } catch (err) {
        console.error("ManageDealers: failed to update principal user", {
          uid,
          dealerId: dealerDocId,
          error: err,
        });
        logVerbose("Principal user document update failed", {
          uid,
          dealerId: dealerDocId,
          error: err,
        });
      }
    },
    [db, userData?.manufacturerId]
  );

  useEffect(() => {
    logVerbose("InnerManageDealers mounted");
    return () => {
      logVerbose("InnerManageDealers unmounted");
    };
  }, []);

  const { dealers, refresh, createDealer, updateDealer, deleteDealer } = useDealersData();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingDealer, setEditingDealer] = useState<Dealer | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuDealer, setMenuDealer] = useState<Dealer | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingDealer, setDeletingDealer] = useState<Dealer | null>(null);

  // map of accountOwner uid -> email for display
  const [ownerEmailMap, setOwnerEmailMap] = useState<Record<string, string>>({});
  const [dealerUsersMap, setDealerUsersMap] = useState<Record<string, DealerUserSummary[]>>({});

  useEffect(() => {
    logVerbose("User data updated", {
      hasUser: Boolean(userData),
      manufacturerId: userData?.manufacturerId ?? null,
      dealerId: userData?.dealerId ?? null,
      role: userData?.role ?? null,
      isAdmin: userData?.isAdmin ?? false,
    });
  }, [userData?.manufacturerId, userData?.dealerId, userData?.role, userData?.isAdmin]);

  useEffect(() => {
    logVerbose("Dealers collection updated", {
      count: dealers?.length ?? 0,
      dealerIds: (dealers ?? []).map((d) => d.id),
    });
  }, [dealers]);

  useEffect(() => {
    const loadOwnerEmails = async () => {
      logVerbose("Loading owner email map request started");
      const uids = Array.from(
        new Set(
          (dealers || [])
            .map((d: any) => d.accountOwner)
            .filter((v: any) => typeof v === "string" && v.trim().length)
        )
      ) as string[];
      logVerbose("Owner email lookup queued", { uids });
      if (!uids.length) {
        setOwnerEmailMap({});
        logVerbose("Owner email map reset because there are no account owners");
        return;
      }
      try {
        const entries: Record<string, string> = {};
        for (const uid of uids) {
          try {
            const userRef = doc(db, "users", uid);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
              const data = userSnap.data() as any;
              const email: string = data.email ?? "";
              entries[uid] = email || uid;
              logVerbose("Resolved owner email", { uid, email: entries[uid] });
            } else {
              entries[uid] = uid;
              logVerbose("Owner document missing", { uid });
            }
          } catch (innerErr) {
            entries[uid] = uid;
            console.warn("ManageDealers: failed to resolve owner email; falling back to uid", { uid, error: innerErr });
          }
        }
    setOwnerEmailMap(entries);
        logVerbose("Owner email map populated", { entries });
      } catch (err) {
        console.error("ManageDealers: failed to load owner emails", err);
        logVerbose("Owner email map failed", { error: err });
      }
    };
    loadOwnerEmails();
  }, [dealers, db]);

  useEffect(() => {
    const loadDealerUsers = async () => {
      if (!Array.isArray(dealers) || !dealers.length) {
        setDealerUsersMap({});
        logVerbose("Dealer users map reset due to empty dealer list");
        return;
      }
      logVerbose("Loading dealer user roster", { dealerCount: dealers.length });
      const usersCollection = collection(db, "users");
      const entries: Record<string, DealerUserSummary[]> = {};
      await Promise.all(
        dealers.map(async (dealer) => {
          const dealerId = dealer.id;
          if (!dealerId) return;
          const filters = [where("dealerId", "==", dealerId)];
          if (userData?.manufacturerId) {
            filters.push(where("manufacturerId", "==", userData.manufacturerId));
          }
          try {
            const qy = query(usersCollection, ...(filters as any));
            const snap = await getDocs(qy);
            const list: DealerUserSummary[] = snap.docs.map((docSnap) => {
              const data = docSnap.data() as Record<string, any>;
              return {
                id: docSnap.id,
                email: data.email,
                name: data.name,
                role: data.role,
                isAdmin: data.isAdmin,
              };
            });
            const accountOwnerId =
              typeof (dealer as any)?.accountOwner === "string"
                ? (dealer as any).accountOwner
                : undefined;
            const nonPrincipal = list.filter(
              (user) => user.id && user.id !== accountOwnerId
            );
            entries[dealerId] = nonPrincipal;
            logVerbose("Dealer roster loaded", {
              dealerId,
              rosterSize: nonPrincipal.length,
            });
          } catch (err) {
            entries[dealerId] = [];
            console.error("ManageDealers: failed to load dealer roster", {
              dealerId,
              error: err,
            });
            logVerbose("Dealer roster load failed", { dealerId, error: err });
          }
        })
      );
      setDealerUsersMap(entries);
    };

    loadDealerUsers();
  }, [dealers, db, userData?.manufacturerId]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Business Name",
        muiTableHeadCellProps: { sx: { px: 1 } },
        muiTableBodyCellProps: { sx: { px: 1 } },
      },
      {
        accessorKey: "contactInfo.email",
        header: "Email",
        Cell: ({ row }: any) => (row.original?.contactInfo?.email ?? ""),
        muiTableHeadCellProps: { sx: { px: 1 } },
        muiTableBodyCellProps: { sx: { px: 1 } },
      },
      {
        accessorKey: "dealerNumber",
        header: "Dealer Number",
        Cell: ({ row }: any) => {
          const mf = userData?.manufacturerId;
          const map = (row.original?.manufacturerDealerNumbers ?? {}) as Record<string, string>;
          return mf ? (map[mf] ?? "") : "";
        },
        muiTableHeadCellProps: { sx: { px: 1 } },
        muiTableBodyCellProps: { sx: { px: 1 } },
      },
      // manufacturerIds are intentionally hidden from the UI
      {
        accessorKey: "accountOwner",
        header: "Principal User",
        Cell: ({ row }: any) => {
          const uid = row.original?.accountOwner as string | undefined;
          if (!uid) return "";
          return ownerEmailMap[uid] ?? uid;
        },
        muiTableHeadCellProps: { sx: { px: 1 } },
        muiTableBodyCellProps: { sx: { px: 1 } },
      },
    ],
    [ownerEmailMap, userData?.manufacturerId]
  );

  const table = useMaterialReactTable({
    columns,
    data: dealers ?? [],
    enableColumnActions: false,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableRowSelection: false,
    enableRowActions: true,
    positionActionsColumn: "last",
    muiTableProps: {
      sx: (t) => ({ elevation: 0, boxShadow: "none" }),
    },
    muiTableHeadRowProps: {
      sx: (t) => ({
        backgroundColor: t.palette.background.paper,
        "& th": {
          borderBottom: `1px solid ${t.palette.divider} !important`,
        },
        "&:hover": {
          backgroundColor: `${t.palette.background.paper} !important`,
        },
      }),
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => row.toggleExpanded(),
      sx: (t) => ({
        backgroundColor: t.palette.background.paper,
        "&:hover": {
          cursor: "pointer",
          backgroundColor: t.palette.action.hover,
        },
        "&.Mui-selected": {
          backgroundColor: t.palette.action.selected,
        },
        "& td": {
          borderBottom: `1px solid ${t.palette.divider} !important`,
        },
        "& td:after": {
          backgroundColor: "transparent !important",
        },
      }),
    }),
    muiBottomToolbarProps: {
      sx: (t) => ({ backgroundColor: t.palette.background.paper }),
    },
    muiTableBodyProps: {
      sx: (t) => ({ backgroundColor: t.palette.background.paper }),
    },
    muiTopToolbarProps: {
      sx: (t) => ({
        display: "flex",
        alignItems: "center",
        backgroundColor: t.palette.background.paper,
        "& > div": { flexDirection: "row-reverse" },
      }),
    },
    renderRowActions: ({ row }) => (
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          openMenu(row.original as Dealer, e);
        }}
        aria-controls={menuAnchorEl ? "row-actions-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={Boolean(menuAnchorEl) ? "true" : undefined}
      >
        <MoreVertIcon />
      </IconButton>
    ),
    renderTopToolbarCustomActions: ({ table: _table }) => (
      <IconButton color="primary" onClick={openAdd}>
        <AddIcon />
      </IconButton>
    ),
    enableExpanding: true,
    displayColumnDefOptions: {
      "mrt-row-expand": {
        size: 48,
        muiTableHeadCellProps: { sx: { px: 0.5 } },
        muiTableBodyCellProps: { sx: { px: 0.5 } },
      },
      "mrt-row-actions": {
        header: "",
        muiTableHeadCellProps: () => ({
          sx: { width: 56, maxWidth: 56, minWidth: 56, px: 1 },
        }),
        muiTableBodyCellProps: {
          sx: { width: 56, maxWidth: 56, minWidth: 56, px: 1 },
        },
      },
    },
    renderDetailPanel: ({ row }) => {
      const dealerId = (row.original as Dealer)?.id ?? "";
      const entries = dealerId ? dealerUsersMap[dealerId] ?? [] : [];
      return (
        <Box sx={{ p: 2, backgroundColor: "background.paper" }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Team Members
          </Typography>
          {entries.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No additional users assigned to this dealer.
            </Typography>
          ) : (
            <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, display: "flex", flexDirection: "column", gap: 0.75 }}>
              {entries.map((user) => (
                <Box
                  key={user.id}
                  component="li"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    border: (t) => `1px solid ${t.palette.divider}`,
                    borderRadius: 1,
                    px: 1.5,
                    py: 1,
                    backgroundColor: "background.default",
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {user.name || user.email || user.id}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user.email || "No email on file"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Role: {user.role ?? "unspecified"}
                    {user.isAdmin ? " (admin)" : ""}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      );
    },
    initialState: { density: "spacious", pagination: { pageIndex: 0, pageSize: 25 } },
  });

  const openAdd = () => {
    setEditingDealer(null);
    setModalOpen(true);
    logVerbose("Add dealer modal opened");
  };

  const openEdit = (d: Dealer) => {
    setEditingDealer(d);
    setModalOpen(true);
    logVerbose("Edit dealer modal opened", { dealerId: d?.id ?? null });
  };

  const openMenu = (d: Dealer, e: React.MouseEvent<HTMLElement>) => {
    setMenuDealer(d);
    setMenuAnchorEl(e.currentTarget);
    logVerbose("Row actions menu opened", { dealerId: d?.id ?? null });
  };
  const closeMenu = () => {
    setMenuDealer(null);
    setMenuAnchorEl(null);
    logVerbose("Row actions menu closed");
  };

  const openDeleteConfirm = (d: Dealer) => {
    setDeletingDealer(d);
    setDeleteConfirmOpen(true);
    closeMenu();
    logVerbose("Delete confirmation opened", { dealerId: d?.id ?? null });
  };
  const closeDeleteConfirm = () => {
    setDeletingDealer(null);
    setDeleteConfirmOpen(false);
    logVerbose("Delete confirmation closed");
  };

  const handleDeleteConfirm = async () => {
    if (!deletingDealer?.id) return;
    try {
      logVerbose("Delete dealer requested", { dealerId: deletingDealer.id });
      await deleteDealer(deletingDealer.id);
      logVerbose("Delete dealer succeeded; refreshing list");
      await refresh();
    } catch (error) {
      console.error("ManageDealers: delete failed", error);
      logVerbose("Delete dealer failed", { dealerId: deletingDealer.id, error });
    } finally {
      closeDeleteConfirm();
    }
  };

  const handleSave = async (payload: FormDealer) => {
    const toSave: any = { ...payload };
    logVerbose("Save handler invoked", {
      editing: Boolean(editingDealer?.id),
      editingId: editingDealer?.id ?? null,
      rawPayload: payload,
    });
    let resolvedAccountOwnerUid: string | null = null;

    // Resolve principal email -> uid for accountOwner
    if (toSave.principalEmail) {
      const email = String(toSave.principalEmail).trim();
      delete toSave.principalEmail;
      if (email.length) {
        const dbHandle = getFirestore();
        const usersRef = collection(dbHandle, "users");
        const emailLower = email.toLowerCase();
        const filterVariants: any[][] = [
          [where("email", "==", email)],
          [where("emailLower", "==", emailLower)],
        ];
        if (userData?.manufacturerId) {
          filterVariants.forEach((filters) =>
            filters.push(where("manufacturerId", "==", userData.manufacturerId))
          );
        }
        let resolvedUid: string | null = null;
        for (const filters of filterVariants) {
          try {
            logVerbose("Attempting principal lookup", { filters });
            const qy = query(usersRef, ...(filters as any));
            const snap = await getDocs(qy);
            if (!snap.empty) {
            const uid = (snap.docs[0].data() as any).id || snap.docs[0].id;
            resolvedUid = uid;
            logVerbose("Principal lookup succeeded", { email, filters, uid });
            resolvedAccountOwnerUid = uid;
            break;
          }
        } catch (lookupErr) {
          console.warn("ManageDealers: principal lookup variant failed", { email, filters, error: lookupErr });
          logVerbose("Principal lookup variant failed", { email, filters, error: lookupErr });
          }
        }
        if (resolvedUid) {
          toSave.accountOwner = resolvedUid;
        } else {
          console.warn("ManageDealers: principal email not found or not visible for current manufacturer; leaving accountOwner unset", { email, mf: userData?.manufacturerId });
          logVerbose("Principal email not resolved", { email, manufacturerId: userData?.manufacturerId ?? null });
        }
      }
    }

    // Map dealerNumber to manufacturerDealerNumbers.<currentManufacturerId>
    const mfId = userData?.manufacturerId;
    if (mfId && typeof toSave.dealerNumber === "string" && toSave.dealerNumber.trim().length) {
      const dn = toSave.dealerNumber.trim();
      delete toSave.dealerNumber;
      toSave.manufacturerDealerNumbers = { [mfId]: dn } as any;
      logVerbose("Mapped dealer number to manufacturer-specific field", { manufacturerId: mfId, dealerNumber: dn });
    } else {
      delete toSave.dealerNumber;
      logVerbose("Dealer number removed from payload", { manufacturerId: mfId ?? null });
    }

    let dealerDocId: string | null = editingDealer?.id ?? null;
    try {
      if (!editingDealer) {
        logVerbose("Creating new dealer", { payload: toSave });
        dealerDocId = await createDealer(toSave);
      } else if (editingDealer.id) {
        logVerbose("Updating dealer", { dealerId: editingDealer.id, payload: toSave });
        await updateDealer(editingDealer.id, toSave);
        dealerDocId = editingDealer.id;
      }
      if (resolvedAccountOwnerUid && dealerDocId) {
        await updatePrincipalUserDoc(resolvedAccountOwnerUid, dealerDocId);
      }
      logVerbose("Dealer mutation succeeded, refreshing data");
      await refresh();
    } catch (err) {
      console.error("ManageDealers: create/update failed", err);
      logVerbose("Dealer mutation failed", { error: err });
      // keep modal open so user can adjust inputs
      return;
    }
  };

  if (!userData?.isAdmin) {
    return (
      <div style={{ padding: 24 }}>
        <h2>Not authorized</h2>
        <p>You must be an administrator to view this page.</p>
      </div>
    );
  }

  return (
    <DashboardLayout>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
        <UserIcon sx={{ fontSize: "2.5rem" }} color="primary" />
        <Typography variant="h4" color="primary" sx={{ fontWeight: 600 }}>
          Dealers
        </Typography>
      </Box>

      <MaterialReactTable table={table} />

      <Menu
        id="row-actions-menu"
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <MenuItem
          onClick={() => {
            if (menuDealer) openEdit(menuDealer);
            closeMenu();
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => menuDealer && openDeleteConfirm(menuDealer)}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog open={deleteConfirmOpen} onClose={closeDeleteConfirm} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Dealer</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete
            {" "}
            <strong>{deletingDealer?.name ?? deletingDealer?.id}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteConfirm}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <DealerModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        dealer={editingDealer ?? undefined}
      />
    </DashboardLayout>
  );
};

const ManageDealers: React.FC = () => {
  return (
    <DealersDataProvider>
      <InnerManageDealers />
    </DealersDataProvider>
  );
};

export default ManageDealers;
